import React, { useEffect, useState } from 'react'
import { HiveServiceClient } from '@api/HiveServiceClient'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import Modal from '@amzn/meridian/modal'
import Text from '@amzn/meridian/text'
import Column from '@amzn/meridian/column'
import Row from '@amzn/meridian/row'
import Divider from '@amzn/meridian/divider'
import Button from '@amzn/meridian/button'
import RadioButton from '@amzn/meridian/radio-button'
import { AtCenteredLoader } from '@amzn/ihm-associate-tools-meridian-components'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { store } from '@atredux/store'
import { setConnectionsFinished } from '@atredux/slices/connectionsSlice'

import { connectionsCalled, isConnectionsEnabled, startCountdown } from '@utils/ConnectionsSurveyCheck'

interface IResponseOptions {
    readonly responseId: string
    readonly text: string
}

interface IContent {
    readonly contentId: string
    readonly contentText: string
    readonly offeringId: string
    readonly ratherNotAnswerOption: IResponseOptions
    readonly responseOptions: IResponseOptions[]
}

interface IScheduledContent {
    readonly content: IContent
    readonly followUpContent: IContent
}
export const AtConnectionsModal = () => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const [scheduledContentList, setScheduledContentList] = useState<IScheduledContent[]>([])
    const [scheduledContent, setScheduledContent] = useState<IScheduledContent | undefined>()
    const [content, setContent] = useState<IContent>()
    const [requestToken, setRequestToken] = useState('')
    const [responseId, setResponseId] = useState<string | undefined>()
    const [modalOpen, setModalOpen] = useState(false)
    const [loading, setLoading] = useState<boolean>(true)

    useEffect(() => {
        if (isConnectionsEnabled()) {
            startCountdown()
            getConnections()
            .then(processScheduledContent)
            .then(connectionsCalled)
            .catch((error) => {
                console.error(`Caught error trying to get connections: ${error}`)
                setConnectionsFinishedState()
            })
        } else {
            console.log('ConnectionsModal - Connections are not enabled. Skipping connections workflow.')
            setConnectionsFinishedState()
        }
    }, [])

    /**
     * Calls Hive.getScheduledPendingContent to get the Connection questions for the day
     *
     * @returns {Promise<any>} Promise containing the result from calling Hive. On a successful response it returns an object formatted  as follows:
     * {
     *     scheduledOfferingContentList: [scheduledContent],
     *     requestToken: 'token'
     * }
     */
    const getConnections = async () => {
        const date = new Date()
        const timezoneNegativeOffset = date.getTimezoneOffset() * -1
        return HiveServiceClient.getInstance().getScheduledPendingContent(EnvironmentDetail.getStoreId(), timezoneNegativeOffset)
    }

    /**
     * Processes the response from the getScheduledPendingContent call. If the response is empty, it returns and no modal is opened. If the response is not
     * empty, it sets the state values needed to render the modal and components. It will shift the scheduledOfferingContentList and set the current content
     * offering to the first question in the list.
     *
     * @returns {Promise<any>} response Promise containing the response from acknowledging the reception of the content, which is empty if successful.
     */
    const processScheduledContent = (response) => {
        const contentList = response.scheduledOfferingContentList
        if (contentList.length < 1) {
            setConnectionsFinishedState()
            return
        }

        const offering = contentList.shift()
        const currentContent = offering.content
        setScheduledContentList(contentList)
        setScheduledContent(offering)
        setContent(currentContent)
        setRequestToken(response.requestToken)
        setModalOpen(true)
        setLoading(false)

        return HiveServiceClient.getInstance().acknowledgeContentReceived(EnvironmentDetail.getStoreId(), currentContent.contentId, response.requestToken, currentContent.offeringId)
    }

    /**
     * Tries to fetch the next primary scheduled content from the scheduledContentList. If the scheduledContentList is empty, it will return undefined and
     * this method will return false indicating there is no more content scheduled for the user. If there is more content then it will set the next content
     * offering as the active content and the appropriate state values, then finally slice the scheduledContentList and return true.
     *
     * @returns {boolean} true if there is more content, false if there is no more content
     */
    const fetchNextScheduledContent = (): boolean => {
        const nextScheduledContent = scheduledContentList[0]

        if (!nextScheduledContent) {
            return false
        }

        setScheduledContentList(scheduledContentList.splice(1))
        setScheduledContent(nextScheduledContent)
        setContent(nextScheduledContent.content)
        setResponseId(null)
        return true
    }

    /**
     * Submits a response to the current question, whether it is a primary question or followup question. PutContentResponse returns the followup question
     * content if there is any the state values will be set according to the response. If there is no followup question, it will then try to fetch the next
     * primary scheduled content from the scheduledContentList by calling fetchNextScheduledContent. If there is no more content, it will close the modal.
     */
    const submitResponse = () => {
        setLoading(true)
        HiveServiceClient.getInstance().putContentResponse(EnvironmentDetail.getStoreId(), content.contentId, responseId, requestToken, content.offeringId)
        .then((response) => {
            if (response.followupQuestion) {
                setContent(response.followupQuestion)
                setRequestToken(response.requestToken)
                setResponseId(null)
            } else if (!fetchNextScheduledContent()) {
                setModalOpen(false)
                setConnectionsFinishedState()
            }
        })
        .catch((error) => {
            console.error(`Error while submitting connections response: ${error}`)
            setConnectionsFinishedState()
        })
        .finally(() => setLoading(false))
    }

    /**
     * Called when the user clicks on the postpone button.
     * Hides the connections modal and sets the connections
     * finished redux state to true.
     */
    const onClickPostpone = () => {
        setModalOpen(false)
        setConnectionsFinishedState()
    }

    /**
     * Sets the connectionsFinished redux state to true.
     */
    const setConnectionsFinishedState = () => {
        console.log('ConnectionsModal - finishing connections workflow.')
        store.dispatch(setConnectionsFinished({ isConnectionsFinished: true }))
    }

    /**
     * Renders the question and answer options for the current question.If there is content to display, it will return a list of RadioButtons with the
     * appropriate values. The RatherNotAnswer choice will be appended to the end of the responses. If there is no content to display, it will return
     * a simple text element as a fallback in edge cases, which should not happen normally. If response options are null for any reason, the option will not
     * be rendered.
     *
     * @returns {JSX.Element}
     */
    const renderAnswerChoices = () => {
        if (scheduledContent) {
            let options = []
            if (content.responseOptions) {
                options = content.responseOptions.map((responseOption) => {
                    if (responseOption) {
                        const currentResponseId = responseOption.responseId
                        return (
                            <Row
                                key={responseOption.responseId}
                            >
                                <RadioButton
                                    checked={responseId === currentResponseId}
                                    onChange={() => setResponseId(currentResponseId)}
                                    value={currentResponseId}
                                >
                                    {responseOption.text}
                                </RadioButton>
                            </Row>
                        )
                    }
                })
            }
            if (content.ratherNotAnswerOption) {
                options.push((
                    <Row
                        key={content.ratherNotAnswerOption.responseId}
                    >
                        <RadioButton
                            checked={responseId === content.ratherNotAnswerOption.responseId}
                            onChange={() => setResponseId(content.ratherNotAnswerOption.responseId)}
                            value={content.ratherNotAnswerOption.responseId}
                        >
                            {content.ratherNotAnswerOption.text}
                        </RadioButton>
                    </Row>
                ))
            }
            return options
        } else {
            return (
                <Row>
                    <Text>{t(bundle, 'jsma-web-common-noContent')}</Text>
                </Row>
            )
        }
    }

    return (
        <Modal
            open={modalOpen}
            scrollContainer="modal"
            width="90vw"
        >
            {loading ? <AtCenteredLoader /> :
            <Column>
                <Row>
                    <div>
                        <img alt={t(bundle, 'jsma-web-common-connectionsLogoAlt')} src={'./images/connections_logo.svg'} />
                    </div>
                </Row>
                <Divider />
                <Row>
                    <Text>{content.contentText}</Text>
                </Row>
                {!!scheduledContent && renderAnswerChoices()}
                <Row>
                    <Button
                        size="large"
                        type="secondary"
                        onClick={onClickPostpone}
                    >
                        {t(bundle, 'migrated-ihm-seaward-survey-postpone-button')}
                    </Button>
                    <Button
                        size="large"
                        type="primary"
                        disabled={!responseId}
                        onClick={submitResponse}
                    >
                        {t(bundle, 'migrated-ihm-seaward-survey-submit-button')}
                    </Button>
                </Row>
            </Column>}
        </Modal>
    )
}
